import { useState } from "react"
import { EDIT_TREE, LIVE_TREE } from "library/constants.mjs"
import { Box, DialogContent, DialogTitle, ToggleButton, ToggleButtonGroup } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound.js"
import { ListItemBox } from "lib/@components/ListItemBox.js"
import { TailoredScheduleBrowser } from "routes/schedule/plugins/custom-schedule-from-tailored/components/tailored-schedule-browser.js"
import { OkCancel } from "lib/@components/okCancel.js"
import { normalizeNodeId } from "routes/schedule/plugins/custom-schedule-from-tailored/lib/normalize-node-id.js"
import { If } from "lib/@components/if.js"
import { ScheduleItemImage } from "routes/schedule/plugins/custom-schedule-from-tailored/components/schedule-item-image.js"

/**
 * A browser used for picking tailored schedules within a dialog.
 *
 * Used in the custom schedules route for the tailored copy option.
 * toggle between live and draft regimes -> opening a regime leads to paths that lead to tailored schedule leaf nodes
 *
 * OkCancel value returns the picked schedule node id, the schedule's id and tree as {treeId, scheduleNodeId, scheduleId}
 * scheduleId is necessary to determine the originating schedule
 */
export function GetTailoredCopyDialog() {
    const [selection, setSelection] = useState(null)
    const [paginationPortal, setPaginationPortal] = useState() // state instead of ref for re-render
    const [selectedTree, setSelectedTree] = useState(LIVE_TREE)

    return (
        <>
            <DialogTitle sx={{ mb: 3 }}>
                <Box>Select the tailored schedule to copy</Box>
            </DialogTitle>
            <DialogContent>
                <ListItemBox spacing={2}>
                    <Box flex={1} />
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={selectedTree}
                        onChange={changeTree}
                        data-cy="tailored-schedule-stage"
                    >
                        <ToggleButton aria-label="live regimes" value={LIVE_TREE} color="success">
                            Live
                        </ToggleButton>
                        <ToggleButton aria-label="draft regimes" value={EDIT_TREE} color="primary">
                            Draft
                        </ToggleButton>
                    </ToggleButtonGroup>
                </ListItemBox>
                <Bound paginationPortal={paginationPortal}>
                    <TailoredScheduleBrowser
                        rootId={selectedTree}
                        selectedSchedule={selection?.schedule}
                        onSelect={setSelection}
                    />
                </Bound>
            </DialogContent>
            <ListItemBox pr={4}>
                <Box flex={1} />
                <Box ref={setPaginationPortal} />
            </ListItemBox>
            <If truthy={selection?.schedule?.id}>
                <ScheduleItemImage schedule={selection?.schedule} />
            </If>
            <OkCancel
                value={{
                    scheduleNodeId: normalizeNodeId(selection?.schedule ?? {}),
                    treeId: selection?.treeId,
                    scheduleId: selection?.schedule?.id,
                }}
                okDisabled={!selection?.schedule?.id}
            />
        </>
    )

    function changeTree(_, tree) {
        if (!tree) return // clicking the active stage yields null

        setSelectedTree(tree)
        setSelection(null) // a selection from the other stage would carry the wrong treeId
    }
}
